// ABOUTME: Shared ticket detail panel rendering the chat thread on the left and the info/attachments toggle on the right.
// ABOUTME: Used by WorkScreen and by MTicketDetailBottom so both surfaces share the same chat + details behaviour.

import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import Subheading from "../../../../../components/theme/text/Subheading";
import { selectUser, selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../utils/permissions";
import {
    useAddSupportTicketMessageMutation,
    useGetSupportTicketQuery,
    useMarkSupportTicketAsSeenMutation,
    useUpdateSupportTicketMutation,
} from "../../../../../newapi/warehouse/supportTicketApiSlice";
import { showToastMessageRequestError } from "../../../../../utils/toasts";
import { sendAndMaybeResolve } from "./chat/sendAndResolve";
import { tokens } from "../../../../../theme";
import { support_ticket_activity_action, support_ticket_status } from "../../../../../utils/staticEnums";
import ChatWindow from "./chat/ChatWindow";
import DetailsTabAttachments from "./tabs/DetailsTabAttachments";
import DetailsTabInfo from "./tabs/DetailsTabInfo";
import PriorityBadge from "./PriorityBadge";
import TicketStatusActions from "./TicketStatusActions";
import { ENV_TYPES } from "../../../../../components/global/Sidebar";

const DETAILS_TABS = {
    INFO: 'info',
    ATTACHMENTS: 'attachments',
};

const TicketDetailPanel = ({ ticketId, height, readOnly, readOnlyFooter, viewer = 'fulfilmentcenter', banner, onTicketClosed }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const user = useSelector(selectUser);

    const [detailsTab, setDetailsTab] = useState(DETAILS_TABS.INFO);
    const [messages, setMessages] = useState([]);

    const { data: ticketDetails } = useGetSupportTicketQuery(ticketId, {
        skip: !ticketId,
    });

    // Either explicitly read-only (e.g. opened from a linked-ticket click) or
    // the ticket itself is CLOSED — both lock down actions/input.
    const isLocked = readOnly || ticketDetails?.data?.status === support_ticket_status.CLOSED;

    const [markAsSeen] = useMarkSupportTicketAsSeenMutation();
    const [addMessage] = useAddSupportTicketMessageMutation();
    const [updateTicket] = useUpdateSupportTicketMutation();

    useEffect(() => {
        if (ticketId) {
            markAsSeen(ticketId);
        }
    }, [ticketId, markAsSeen]);

    useEffect(() => {
        if (!ticketDetails?.data?.messages) return;

        const converted = ticketDetails.data.messages.flatMap((msg) => {
            // message_type drives bubble alignment + which renderer to use (always
            // fulfilmentcenter/client by author side). isInternal is a separate flag
            // that controls the "internal note" yellow styling and grouping.
            const messageType = msg.user?.isWarehouse ? 'fulfilmentcenter' : 'client';
            const isInternal = msg.isInternal === true;
            const senderName = msg.user?.name || 'Unknown';
            const timestamp = new Date(msg.createdAt);

            const entries = [];

            if (msg.content) {
                // Backend stores TipTap doc as a JSON string; parse it back to an object
                // so generateHTML can render it. Fall back to the raw string for legacy/plain content.
                let parsedContent = msg.content;
                if (typeof msg.content === 'string') {
                    try {
                        parsedContent = JSON.parse(msg.content);
                    } catch {
                        parsedContent = msg.content;
                    }
                }

                // Activity messages (assignment, status change, linking) carry their own
                // structured content with a known type — pass them through as-is.
                // Everything else is treated as a TipTap text doc.
                const isActivity =
                    parsedContent &&
                    typeof parsedContent === 'object' &&
                    Object.values(support_ticket_activity_action).includes(parsedContent.type);

                entries.push({
                    user: msg.user,
                    message_type: messageType,
                    isInternal,
                    senderName,
                    timestamp,
                    content: isActivity
                        ? parsedContent
                        : { type: 'text', content: parsedContent },
                });
            }

            if (msg.attachments?.length > 0) {
                entries.push({
                    user: msg.user,
                    message_type: messageType,
                    isInternal,
                    senderName,
                    timestamp,
                    content: {
                        type: 'attachments',
                        attachments: msg.attachments.map((att) => ({
                            url: att.url,
                            fileName: att.originalName,
                            type: att.mimeType?.startsWith('image/') ? 'image' : 'document',
                        })),
                    },
                });
            }

            return entries;
        });

        setMessages(converted);
    }, [ticketDetails, user?.id]);

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];
    // Relation users need the portal manage right to resolve their tickets;
    // the backend enforces the same privilege on the update endpoint.
    const hasRpSupportManage = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.RP_SUPPORT_MANAGE])
    );

    const handleSendMessage = async ({ content, attachments, isInternal, resolveAfter }) => {
        if (!ticketId) return;

        try {
            const formData = new FormData();

            // Multer requires non-file fields to be appended BEFORE files;
            // fields appearing after the first file may not end up in req.body.
            if (content) {
                const contentString = typeof content === 'string' ? content : JSON.stringify(content);
                formData.append('content', contentString);
            }
            formData.append('isInternal', isInternal === true ? 'true' : 'false');

            if (attachments && attachments.length > 0) {
                attachments.forEach((att) => {
                    formData.append('files', att.file);
                });
            }

            const result = await sendAndMaybeResolve({
                send: () => addMessage({ id: ticketId, data: formData }).unwrap(),
                resolve: () => updateTicket({
                    id: ticketId,
                    data: { status: support_ticket_status.RESOLVED },
                }).unwrap(),
                resolveAfter,
            });
            if (result.resolveError) {
                showToastMessageRequestError(t("update_failed"), t, result.resolveError);
            }

            const newMessages = [];
            const timestamp = new Date();

            if (content) {
                newMessages.push({
                    message_type: viewer,
                    isInternal: isInternal === true,
                    senderName: user.name,
                    timestamp,
                    content: { type: 'text', content: content },
                });
            }

            if (attachments && attachments.length > 0) {
                newMessages.push({
                    message_type: viewer,
                    isInternal: isInternal === true,
                    senderName: user.name,
                    timestamp,
                    content: {
                        type: 'attachments',
                        attachments: attachments.map((att) => ({
                            url: att.previewUrl || URL.createObjectURL(att.file),
                            fileName: att.fileName,
                            type: att.type,
                        })),
                    },
                });
            }

            if (newMessages.length > 0) {
                setMessages((prev) => [...prev, ...newMessages]);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                height: height ?? 1,
                width: 1,
            }}
        >
            {/* Chat side */}
            <Box
                sx={{
                    flex: 1,
                    borderRight: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        px: 4,
                        pt: 4,
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            background: generateColorFromTitle(ticketDetails?.data?.type ?? "", theme.palette.mode, 90),
                            "&:after": {
                                content: "''",
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: 0,
                                bottom: 0,
                                borderRadius: 4,
                                zIndex: -1,
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                flexShrink: 0,
                                borderRadius: 2.5,
                                background: generateIconColorFromTitle(ticketDetails?.data?.type ?? "", theme.palette.mode),
                            }}
                        />
                    </Box>
                    <PriorityBadge
                        priority={ticketDetails?.data?.priority}
                        ticketId={ticketId}
                        disabled={isLocked || viewer !== 'fulfilmentcenter'}
                        sx={{ ml: 1, mr: 1.5 }}
                    />

                    <Subheading color={colors.txt['secondary']} sx={{ mr: 1 }}>
                        #{ticketDetails?.data?.ticketNumber}
                    </Subheading>
                    <Subheading>
                        {ticketDetails?.data?.title}
                    </Subheading>
                    {!isRelation && !isLocked && (
                        <Box sx={{ ml: 'auto' }}>
                            <TicketStatusActions
                                ticket={ticketDetails?.data}
                                onStatusChange={(status) => {
                                    if (status === support_ticket_status.CLOSED) {
                                        onTicketClosed?.();
                                    }
                                }}
                            />
                        </Box>
                    )}
                    {isRelation && !isLocked && hasRpSupportManage && (
                        <Box sx={{ ml: 'auto' }}>
                            <TicketStatusActions ticket={ticketDetails?.data} relationMode />
                        </Box>
                    )}
                </Box>

                {banner}
                <ChatWindow
                    onSend={handleSendMessage}
                    messages={messages}
                    colors={colors}
                    theme={theme}
                    viewer={viewer}
                    readOnly={isLocked}
                    readOnlyFooter={readOnlyFooter}
                    // Send & resolve in one go, on a not-yet-resolved ticket:
                    // warehouse agents always, relation users with the portal
                    // manage right (the backend enforces the same privilege).
                    allowResolveOnSend={
                        !isLocked &&
                        ticketDetails?.data?.status !== support_ticket_status.RESOLVED &&
                        ((!isRelation && viewer === 'fulfilmentcenter') ||
                            (isRelation && hasRpSupportManage))
                    }
                />
            </Box>

            {/* Details side */}
            <Box
                sx={{
                    width: 450,
                    flexShrink: 0,
                    minHeight: 0,
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    "@media screen and (max-width: 63.9375em)": {
                        width: 1,
                    },
                }}
            >
                <ToggleButtonGroup
                    value={detailsTab}
                    exclusive
                    onChange={(_, newValue) => newValue && setDetailsTab(newValue)}
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        borderRadius: 5,
                        p: 0.5,
                        gap: 0.5,
                        height: 40,
                        flexShrink: 0,
                        "& .MuiToggleButtonGroup-grouped": {
                            border: 0,
                            borderRadius: "12px !important",
                            flex: 1,
                            textTransform: 'none',
                            borderRadius: 5,
                            fontSize: 14,
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 400,
                            color: `${colors.txt['secondary']} !important`,
                            py: 1,
                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                            "&.Mui-selected": {
                                color: colors.txt['primary'],
                                fontWeight: 600,
                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? "none" : `1px solid ${colors.grey[100]}`,
                                boxShadow: theme.palette.mode === "dark" ? "none" : `0px 2px 4px ${colors.grey[200]}`,
                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"],
                            },
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                            },
                        },
                    }}
                >
                    <ToggleButton value={DETAILS_TABS.INFO}>
                        {t("main_info")}
                    </ToggleButton>
                    <ToggleButton value={DETAILS_TABS.ATTACHMENTS}>
                        {t("attachments")}
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Scrolls under the tab switcher, which stays pinned above it.
                    Sections keep flexShrink 0 so they scroll at full height
                    instead of being squashed to fit. */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        mt: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        "& > *": {
                            flexShrink: 0,
                        },
                        "@media screen and (max-width: 63.9375em)": {
                            overflowY: 'visible',
                        },
                    }}
                >
                    {detailsTab === DETAILS_TABS.INFO && (
                        <DetailsTabInfo ticket={ticketDetails?.data} messages={messages} colors={colors} theme={theme} locked={isLocked} />
                    )}

                    {detailsTab === DETAILS_TABS.ATTACHMENTS && (
                        <DetailsTabAttachments messages={messages} colors={colors} theme={theme} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default TicketDetailPanel;
